import { BrowserBookingMock } from "@/components/mockups/Mockups";
import { AudienceBlock, FeatureGrid } from "@/components/sections/ServiceBlocks";
import { FaqSection } from "@/components/sections/FaqSection";
import { FinalCta } from "@/components/sections/FinalCta";
import { TestimonialsSection } from "@/components/sections/TestimonialsSection";
import { FreeAuditButton, WhatsAppButton } from "@/components/ui/Cta";
import { PageHero } from "@/components/ui/PageHero";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { SEO_WEB_SERVICE_EN } from "@/config/seo.en";
import { SEO_WEB_SERVICE_ES } from "@/config/seo.es";
import { useContent } from "@/data/content";
import { getMockups } from "@/data/mockups";
import { usePageSeo } from "@/hooks/usePageSeo";
import { useLocale } from "@/i18n/useLocale";

export function WebServicePage() {
  usePageSeo("/pagina-web");
  const locale = useLocale();
  const page = useContent().webServicePage;
  const crumb = (locale === "en" ? SEO_WEB_SERVICE_EN : SEO_WEB_SERVICE_ES).breadcrumbWeb;

  return (
    <main id="main-content" tabIndex={-1} className="outline-none">
      <PageHero
        crumb={crumb}
        title={
          <>
            {page.hero.title} <span className="text-teal-strong">{page.hero.titleAccent}</span>
          </>
        }
        lead={<p>{page.hero.subtitle}</p>}
        actions={
          <>
            <WhatsAppButton location="web_hero" variant="primary" label={page.cta.buttonLabel} message={page.cta.whatsappMessage} />
            <FreeAuditButton location="web_hero" variant="outline" />
          </>
        }
        aside={<BrowserBookingMock {...getMockups(locale).browser} />}
      >
        <ul className="hero-rise mt-10 flex flex-wrap gap-x-6 gap-y-2 text-ink-soft" style={{ ["--delay" as string]: "240ms" }}>
          {page.hero.highlights.map((item) => (
            <li key={item} className="node">
              {item}
            </li>
          ))}
        </ul>
      </PageHero>

      <FeatureGrid
        id="web-includes"
        title={page.includes.title}
        lead={page.hero.closing}
        items={page.includes.items}
        footer={<p className="font-display text-xl text-brand-primary">{page.includes.summary}</p>}
      />

      <section aria-labelledby="web-optimization" className="on-navy light-pool-navy bg-brand-primary text-white [--pool-x:85%] [--pool-y:0%]">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
          <SectionHeader id="web-optimization" tone="light" title={page.optimization.title} lead={page.optimization.subtitle} />
          <ul className="mt-14 grid gap-px overflow-hidden rounded-[1.75rem] bg-white/12 sm:grid-cols-2 lg:grid-cols-3">
            {page.optimization.items.map((item, i) => (
              <li
                key={item.title}
                data-reveal=""
                style={{ ["--reveal-delay" as string]: `${(i % 3) * 80}ms` }}
                className="bg-brand-primary p-7"
              >
                <h3 className="font-display text-xl font-semibold">{item.title}</h3>
                <p className="mt-2 leading-relaxed text-on-navy-muted">{item.desc}</p>
              </li>
            ))}
          </ul>
          <div className="mt-12 flex flex-col gap-4 lg:flex-row lg:items-baseline lg:gap-10">
            <p data-reveal="" className="font-display text-2xl font-semibold text-teal-light sm:text-3xl">
              {page.optimization.calloutLines.join(" ")}
            </p>
            <p data-reveal="" className="text-on-navy-muted">
              {page.optimization.calloutBody}
            </p>
          </div>
        </div>
      </section>

      <AudienceBlock title={page.audience.title} items={page.audience.items} />

      <section aria-labelledby="web-pillars" className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
          <SectionHeader id="web-pillars" title={page.cta.philosophy} />
          <ul className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {page.cta.pillars.map((pillar, i) => (
              <li
                key={pillar.title}
                data-reveal=""
                style={{ ["--reveal-delay" as string]: `${i * 70}ms` }}
                className="border-t-2 border-brand-secondary pt-5"
              >
                <h3 className="font-display text-xl font-semibold text-brand-primary">{pillar.title}</h3>
                <p className="mt-2 text-ink-soft">{pillar.desc}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <TestimonialsSection service="pagina-web" />
      <FaqSection path="/pagina-web" tone="bg" />
      <FinalCta
        location="web_final"
        body={page.cta.description}
        whatsappLabel={page.cta.buttonLabel}
        whatsappMessage={page.cta.whatsappMessage}
      />
    </main>
  );
}
